import React from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Box, Button, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import { omit } from 'lodash'
import PropTypes from 'prop-types'

import NavLink from 'components/FwdRefNavLink'

const useStyles = makeStyles((theme) => {
  const activeColors = {
    background: 'rgba(0, 125, 170, 0.08)',
    color: theme.palette.common.black,
  }
  const hide = { display: 'none' }
  const show = { display: 'block' }
  return {
    activeIcon: { ...hide },
    inactiveIcon: { ...show },
    root: {
      borderRadius: theme.spacing(2),
      padding: '12px 20px',
      width: '90px',
      height: '60px',
      color: 'rgba(0, 0, 0, 0.54)',
      margin: theme.spacing(1),

      '&:hover, &:focus': {
        textDecoration: 'none',
        color: theme.palette.primary.main,
      },
      '&:hover $activeIcon, &:focus $activeIcon': { ...show },
      '&:hover $inactiveIcon, &:focus $inactiveIcon': { ...hide },
      '&:focus:not(:focus-visible)': {
        outline: '0',
        boxShadow: 'none',
      },
    },
    active: {
      ...activeColors,
      '&:hover, &:focus': {
        ...activeColors,
      },
      '& $inactiveIcon': {
        ...hide,
      },
      '& $activeIcon': {
        ...show,
      },
    },
  }
})

/**
 * This component provides button-like styling to links in top navigation
 * @param {object} props
 * @prop {bool} active
 * @prop {node} children
 * @prop {string} to
 * @prop {string} badgeContent
 * @prop {string} title
 * @prop {string} label
 * @prop {any} activeIcon
 * @prop {any} inactiveIcon
 * @returns MainTabItem component for top-navigation
 */
const MainTabItem = ({
  active,
  children,
  url,
  badgeContent,
  badgeVariant,
  title,
  label,
  activeIcon,
  inactiveIcon,
  ...rest
}) => {
  const classes = useStyles()
  const linkProps = !url
    ? {}
    : {
        to: url,
        component: NavLink,
        activeClassName: classes.active,
      }
  const additionalProps = omit(rest, ['dispatch', 'staticContext'])

  return (
    <Box
      title={label}
      display="inline-block"
      className="e2e-header-navigation-item"
    >
      <Button
        className={cn(classes.root, {
          [classes.active]: active,
        })}
        aria-label={label}
        {...linkProps}
        {...additionalProps}
      >
        <Box textAlign="center">
          <Badge
            badgeContent={badgeContent}
            variant={badgeVariant}
            color="error"
          >
            {activeIcon && (
              <FontAwesomeIcon
                className={classes.activeIcon}
                icon={activeIcon}
                size="lg"
              />
            )}
            {inactiveIcon && (
              <FontAwesomeIcon
                className={classes.inactiveIcon}
                icon={inactiveIcon}
                size="lg"
              />
            )}
            {children}
          </Badge>
          <Box
            mt={1}
            height="12px"
            width="71px"
            text-align="center"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="caption">{title}</Typography>
          </Box>
        </Box>
      </Button>
    </Box>
  )
}

MainTabItem.propTypes = {
  location: PropTypes.object,
  match: PropTypes.object,
  active: PropTypes.bool,
  children: PropTypes.node,
  url: PropTypes.string,
  badgeContent: PropTypes.number,
  badgeVariant: PropTypes.string,
  title: PropTypes.node,
  label: PropTypes.string,
  activeIcon: PropTypes.any,
  inactiveIcon: PropTypes.any,
}

export default MainTabItem
